import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import RightPage from 'components/util/side-by-side-page-component';
import StatementContentPanelComponentHOC from 'containers/feature/statement-content/statement-content-panel-container';
import ConditionalRender from 'components/util/conditional-render-component';
import Revision from 'models/api/revision-api-model';
import SelectedStatement from 'models/api/selected-statement-model';
import classnames from 'classnames';
import { connect } from 'react-redux';
import {
  initStatementContent,
  setTargetSyncScrollId,
} from 'store/actions/statement-content-actions';
import {
  setElementSelectModeSideBySideIfPossible,
  clearModeIfPossible,
  setElementSelectModeBatchIfPossible,
} from 'store/actions/modes-actions';
import { fetchSelectedStatement } from 'store/actions/selected-statement-actions';
import ZoomControlHOC from 'higher-order-components/zoom-control-hoc-component';
import withURLRouter from 'withURLRouter';
import Tooltip from 'components/common/tool-tip-component';
import { ReactComponent as StatementNavIcon } from 'icons/statement-navigator.svg';
import { SIDE_BY_SIDE_VIEW_STATEMENT_NAV_ICON_SIZE } from '../left-side/_left-side-statement-view';
import SideBySideStatementNavFlyout from '../side-by-side-toolkits/side-by-side-statement-nav/side-by-side-statement-nav-flyout';
import {
  onRightStatementNavSectionClick,
  setSideBySideSyncScroll,
} from 'store/actions/side-by-side-statement/side-by-side-statement-actions';
import { isBothLegacyOrOCRInSideBySideView } from 'utils/sync-scroll-utils';
import { ContentSectionMap } from 'models/api/content-section-map-api-model';
import { SectionTreeList } from 'models/api/section-tree-list-model';
import { SectionIdList } from 'models/api/section-id-list-model';
import { SectionReviewList } from 'models/api/section-review-list-api-model';
import { FormattedMessage } from 'react-intl';
import { ELEMENT_SELECT_MODES } from 'constants/feature/modes-constants';
import { setOcrCurrentViewPageNumberDispatch } from 'store/actions/ocr-current-view-page-number-actions';
import StatementContentPanelContainerOcr from 'containers/feature/statement-content/statement-content-panel-container-ocr';
import OCRNavigationModel from 'models/api/ocr-navigation-api-model';
import {
  RIGHT_VIEW_DOCUMENT_ID,
  SIDE_BY_SIDE_VIEW_OCR_COMBINATION_STATEMENT,
  SIDE_BY_SIDE_VIEW_RIGHT_BLOCK,
  SIDE_BY_SIDE_COMPARE_MODE,
  AUTO_TIEOUT_COMPARE_MODE,
  TRAIL_BALANCE_MODE,
} from 'constants/feature/statement-content-constants';
import { isNullOrUndefined } from 'utils/object-utils';
import { syncSideBySideScrollIntoView } from 'utils/sync-scroll-utils';
import SideBySideViewMode from 'models/api/side-by-side-view-mode-modal';
import { setAutoTieoutStatusForBanner } from 'store/actions/auto-tieout/workpaper-details-action';
import BatchBanner from 'pages/batch-banner';
import { getContentPanelHeight } from 'constants/feature/statement-content-constants';

export const RIGHT_STATEMENT_PANEL = 'right-statement-panel';
const SCROLL_TIMEOUT = 150;
const RIGHT_OCR_BLOCK_ID = 'statement-content-panel-page-';

export class RightSideView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // indicates to child components that the user is currently scrolling the content panel
      // i.e. more sections are being revealed
      isContentPanelScrolling: false,
    };
    this.rightPanelRef = createRef();
    this._scrollingTimeout = null;
    this._scrollingInitialized = false;
    this._ctrlPressed = false;
  }
  componentDidMount() {
    this._initRightSideStatement();
  }

  _initRightSideStatement = async () => {
    const { initStatementContent, params, revision } = this.props;
    if (revision && !revision.isLoaded) {
      initStatementContent(params);
    }
  };

  componentDidUpdate(prevProps, prevState) {
    const {
      revision,
      selectedStatement,
      params,
      fetchSelectedStatement,
      setTargetSyncScrollId,
      syncScrollIds,
      isSnapshotReadOnly,
    } = this.props;

    if (revision.isLoaded && selectedStatement.isLoaded) {
      // Need to ensure revision and selected statement is loaded (thus center panel is rendered) before attaching event listener
      this.rightPanelRef.current &&
        this.rightPanelRef.current.addEventListener(
          'scroll',
          this._scrollDetection,
        );
      this._initSideBySideCtrlListeners();
    }

    // Re-attach listeners when isSnapshotReadOnly changes to avoid stale closure
    if (prevProps.isSnapshotReadOnly !== isSnapshotReadOnly) {
      this._cleanUpSideBySideCtrlListeners();
      this._initSideBySideCtrlListeners();
    }

    if (prevProps.params.statementId !== params.statementId) {
      fetchSelectedStatement(params);
    }

    if (revision.isLoaded && selectedStatement.isLoaded) {
      if (
        isNullOrUndefined(syncScrollIds.targetId) ||
        prevProps.syncScrollIds.targetId !== syncScrollIds.targetId
      ) {
        const sourceStatementId = selectedStatement.isOCR
          ? RIGHT_VIEW_DOCUMENT_ID
          : SIDE_BY_SIDE_VIEW_RIGHT_BLOCK;
        setTargetSyncScrollId(sourceStatementId);
      }
    }
  }

  componentWillUnmount() {
    if (this.rightPanelRef && this.rightPanelRef.current) {
      this.rightPanelRef.current.removeEventListener(
        'scroll',
        this._scrollDetection,
      );
    }
    this._cleanUpSideBySideCtrlListeners();
    window.clearTimeout(this._scrollingTimeout);
    this._scrollingTimeout = null;
  }

  _scrollDetection = (event) => {
    const { isContentPanelScrolling } = this.state;
    if (!isContentPanelScrolling) {
      this.setState({
        isContentPanelScrolling: true,
      });
    }
    window.clearTimeout(this._scrollingTimeout);
    this._scrollingTimeout = setTimeout(() => {
      this.setState({
        isContentPanelScrolling: false,
      });
    }, SCROLL_TIMEOUT);
  };

  getStatementHeader = (selectedStatement, revision) => {
    const currentStatementWithRevisionLabel =
      selectedStatement &&
      selectedStatement.statement &&
      revision &&
      `${selectedStatement.statement.statementName}  V${revision.revisionNumber}  `;
    return (
      <div className={`${RIGHT_STATEMENT_PANEL}__currentStatement`}>
        {currentStatementWithRevisionLabel}
        <div className={`${RIGHT_STATEMENT_PANEL}__currentLabel`}>
          <FormattedMessage id="side-by-side-view.compare.right-panel.currentLabel" />
        </div>
      </div>
    );
  };

  _initSideBySideCtrlListeners = () => {
    window.addEventListener('keydown', this._ctrlPressListener);
    window.addEventListener('keyup', this._ctrlReleaseListener);
  };

  _cleanUpSideBySideCtrlListeners = () => {
    window.removeEventListener('keydown', this._ctrlPressListener);
    window.removeEventListener('keyup', this._ctrlReleaseListener);
  };

  _ctrlPressListener = (event) => {
    const {
      setElementSelectModeSideBySideIfPossible,
      clearSideBySideModeIfPossible,
      isSideBySideModeSelected,
      numberOfSourceElementSelected,
      numberOfTargetElementSelected,
      sideBySideViewMode,
      setElementSelectModeBatchIfPossible,
      isSnapshotReadOnly,
      isBatchModeSelected,
      selectedStatement,
      editPermissionAutotieout,
    } = this.props;
    switch (event.key) {
      case 'Control':
        if (
          !isSideBySideModeSelected &&
          !isBatchModeSelected &&
          numberOfSourceElementSelected > numberOfTargetElementSelected &&
          sideBySideViewMode.viewMode !== AUTO_TIEOUT_COMPARE_MODE &&
          sideBySideViewMode.viewMode !== TRAIL_BALANCE_MODE
        ) {
          setElementSelectModeSideBySideIfPossible({
            isLeftStatementView: false,
          });
          this._ctrlPressed = true;
        } else if (
          !isSideBySideModeSelected &&
          !isBatchModeSelected &&
          !selectedStatement?.data?.readOnlyOverride &&
          editPermissionAutotieout &&
          ((sideBySideViewMode.viewMode === TRAIL_BALANCE_MODE &&
            !isSnapshotReadOnly) ||
            sideBySideViewMode.viewMode === AUTO_TIEOUT_COMPARE_MODE)
        ) {
          setElementSelectModeBatchIfPossible();
          this._ctrlPressed = true;
        }
        break;
      default:
        if (
          this._ctrlPressed &&
          !isSideBySideModeSelected &&
          sideBySideViewMode.viewMode !== AUTO_TIEOUT_COMPARE_MODE &&
          sideBySideViewMode.viewMode !== TRAIL_BALANCE_MODE
        ) {
          clearSideBySideModeIfPossible();
          this._ctrlPressed = false;
        }
        break;
    }
  };

  _ctrlReleaseListener = (event) => {
    const { clearSideBySideModeIfPossible, isBatchModeSelected } = this.props;
    if (event.key === 'Control' && !isBatchModeSelected) {
      clearSideBySideModeIfPossible();
      this._ctrlPressed = false;
    }
  };

  navigateToSelectedPage = (page) => {
    const {
      syncScrollIds,
      sideBySideSyncScroll,
      selectedStatement,
      leftSelectedStatement,
    } = this.props;
    const isLeftSideView = true;
    if (
      sideBySideSyncScroll &&
      isBothLegacyOrOCRInSideBySideView(
        selectedStatement,
        leftSelectedStatement,
      ) === SIDE_BY_SIDE_VIEW_OCR_COMBINATION_STATEMENT
    ) {
      syncSideBySideScrollIntoView({
        sectionId: page,
        isLeftView: isLeftSideView,
        syncScrollIds,
        sideBySideSyncScroll,
        selectedStatement,
        leftSelectedStatement,
      });
    } else {
      setOcrCurrentViewPageNumberDispatch(page, isLeftSideView);
      const element = document.getElementById(`${RIGHT_OCR_BLOCK_ID}${page}`);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 250);
      }
    }
  };

  render() {
    const {
      params,
      revision,
      selectedStatement,
      isSourceStatementLoaded,
      isSwapped,
      sectionTreeList,
      onRightStatementNavSectionClick,
      contentSectionMap,
      sectionIdList,
      currentSectionIds,
      sectionReview,
      ocrCurrentViewPageNumber,
      setOcrCurrentViewPageNumberDispatch,
      statementOCRNavigation,
      sideBySideSyncScroll,
      rightStatementNavPanel,
      setRightStatementNavPanel,
      sideBySideViewMode,
      tbWorkFlowStatus,
    } = this.props;
    const { isContentPanelScrolling } = this.state;
    const isOCR = selectedStatement.isLoaded && selectedStatement.isOCR;
    const _isStatementNavButtonSelected =
      this.props.selectedStatement &&
      this.props.selectedStatement.isLoaded &&
      this.props.revision &&
      this.props.revision.isLoaded &&
      rightStatementNavPanel;

    const panelHeight = getContentPanelHeight({
      sideBySideViewMode,
      isSourceStatementLoaded,
      tbWorkFlowStatus,
    });

    return (
      <div
        className={classnames(
          `${RIGHT_STATEMENT_PANEL} ${RIGHT_STATEMENT_PANEL}__split`,
        )}
      >
        <div
          className={classnames(
            `${RIGHT_STATEMENT_PANEL}__statement-header-wrap`,
          )}
        >
          <div
            className={classnames(
              `${RIGHT_STATEMENT_PANEL}__statement-header`,
              {
                [`${RIGHT_STATEMENT_PANEL}__statement-header__swap`]: isSwapped,
              },
            )}
          >
            <Tooltip
              id={`${RIGHT_STATEMENT_PANEL}__statement-nav-tooltip`}
              text="toolkit.icon.statement-nav.tooltip"
            >
              <button
                id={`${RIGHT_STATEMENT_PANEL}__statement-nav-button-id`}
                className={classnames(
                  `${RIGHT_STATEMENT_PANEL}__statement-nav-button`,
                  {
                    [`${RIGHT_STATEMENT_PANEL}__statement-nav-button--selected`]:
                      _isStatementNavButtonSelected,
                    [`${RIGHT_STATEMENT_PANEL}__statement-header__statement-nav-button`]:
                      !isSwapped,
                  },
                )}
                onClick={() =>
                  setRightStatementNavPanel(!rightStatementNavPanel)
                }
              >
                <StatementNavIcon
                  className={classnames(`${RIGHT_STATEMENT_PANEL}__nav-icon`)}
                  width={SIDE_BY_SIDE_VIEW_STATEMENT_NAV_ICON_SIZE}
                  height={SIDE_BY_SIDE_VIEW_STATEMENT_NAV_ICON_SIZE}
                />
              </button>
            </Tooltip>
            <span
              className={`${RIGHT_STATEMENT_PANEL}__statement-header__title`}
            >
              {selectedStatement &&
                selectedStatement.statement &&
                revision &&
                this.getStatementHeader(selectedStatement, revision)}
            </span>
          </div>
        </div>
        <div
          className={classnames(`${RIGHT_STATEMENT_PANEL}__container`, {
            [`${RIGHT_STATEMENT_PANEL}__container__swap`]: isSwapped,
          })}
        >
          <RightPage
            style={{
              height: `calc(100vh - ${panelHeight}px - var(--opm-banner-height, 0px))`,
              minHeight: `calc(100vh - ${panelHeight}px - var(--opm-banner-height, 0px))`,
            }}
            sideClassName={classnames(
              `${RIGHT_STATEMENT_PANEL}__right-page-data`,
            )}
            className={`${RIGHT_STATEMENT_PANEL}__data`}
          >
            <ConditionalRender dependencies={[revision, selectedStatement]}>
              <div
                ref={this.rightPanelRef}
                className={classnames(
                  `${RIGHT_STATEMENT_PANEL}__content-section`,
                  {
                    [`${RIGHT_STATEMENT_PANEL}__content-section-ocr`]: isOCR,
                  },
                )}
                id={SIDE_BY_SIDE_VIEW_RIGHT_BLOCK}
              >
                <BatchBanner />
                {isOCR ? (
                  <StatementContentPanelContainerOcr
                    urlParams={params}
                    isLeftView={false}
                    setOcrCurrentViewPageNumberDispatch={
                      setOcrCurrentViewPageNumberDispatch
                    }
                    sideBySideSyncScroll={sideBySideSyncScroll}
                    ocrCurrentViewPageNumber={ocrCurrentViewPageNumber}
                    isOCR={isOCR}
                  />
                ) : (
                  <StatementContentPanelComponentHOC
                    urlParams={params}
                    centerPanelRef={this.rightPanelRef}
                    isContentPanelScrolling={isContentPanelScrolling}
                    leftSideBySideView={false}
                  />
                )}
                <ZoomControlHOC
                  isSideBySidePage
                  isLeftSideView={false}
                  shouldDisplayExpandIcon={false}
                />
              </div>
            </ConditionalRender>
            <SideBySideStatementNavFlyout
              show={rightStatementNavPanel}
              onSectionClick={onRightStatementNavSectionClick}
              sectionTreeList={sectionTreeList}
              contentSectionMap={contentSectionMap}
              sectionIdList={sectionIdList}
              selectedStatement={selectedStatement}
              currentSectionIds={currentSectionIds}
              sectionReview={sectionReview}
              toggleStatementNavPanel={() =>
                setRightStatementNavPanel(!rightStatementNavPanel)
              }
              statementOCRNavigationData={statementOCRNavigation}
              navigateToSelectedPage={this.navigateToSelectedPage}
              projectId={params.projectId}
              ocrCurrentViewPageNumber={ocrCurrentViewPageNumber}
            />
          </RightPage>
        </div>
      </div>
    );
  }
}
RightSideView.propTypes = {
  /** Action to initialize statement page data */
  initStatementContent: PropTypes.func.isRequired,
  /** Currently selected revision */
  revision: PropTypes.instanceOf(Revision).isRequired,
  /** function fired to fetch statement details */
  fetchSelectedStatement: PropTypes.func.isRequired,
  /** Currently selected statement */
  selectedStatement: PropTypes.instanceOf(SelectedStatement).isRequired,
  /** Object containing cached section id */
  contentSectionMap: PropTypes.instanceOf(ContentSectionMap),
  /** Tree list of section hirearchy */
  sectionTreeList: PropTypes.instanceOf(SectionTreeList),
  /** Function fired on section click  */
  onRightStatementNavSectionClick: PropTypes.func.isRequired,
  /** Api model of all the section id's */
  sectionIdList: PropTypes.instanceOf(SectionIdList).isRequired,
  /** contains list of section reviews */
  sectionReview: PropTypes.instanceOf(SectionReviewList),
  /** OCR statement Navigation data(thumbnails) */
  statementOCRNavigation: PropTypes.instanceOf(OCRNavigationModel),
  /** side by side view compare mode */
  sideBySideViewMode: PropTypes.instanceOf(SideBySideViewMode),
  /** URL parameters */
  params: PropTypes.object.isRequired,
  /** Flag to show auto tieout info banner */
  showAutoTieoutInfoBanner: PropTypes.bool,
  /** set show auto tieout info banner */
  setShowAutoTieoutInfoBanner: PropTypes.func.isRequired,
  /** Function to initiate auto tieout process */
  initiateAutoTieout: PropTypes.func,
  /** Auto tieout workflow status */
  workFlowStatus: PropTypes.object,
  /** Auto tieout workflow status */
  tbWorkFlowStatus: PropTypes.object,
};

const mapStateToProps = (state) => {
  const {
    data,
    data: {
      revision,
      leftRevision,
      selectedStatement,
      leftSelectedStatement,
      projectUsersList,
      sectionTreeList,
      statementContent: {
        sectionsCache: { contentSectionMap },
        sectionIdList,
      },
      sectionPanel,
      editPermission: { editPermissionAutotieout },
    },
    ui: {
      statementPage: {
        currentSectionIds,
        syncScrollIds,
        modes: { ocrCurrentViewPageNumber, sideBySideSyncScroll, selectMode },
        statementNavigatorPanel: { statementOCRNavigation },
      },
      sideBySideView: {
        sideBySideElementMap,
        sideBySideViewMode,
        autoTieout: { workFlowStatus },
        trialBalance: { isSnapshotReadOnly, tbWorkFlowStatus },
      },
    },
  } = state;

  return {
    revision,
    leftRevision,
    selectedStatement,
    leftSelectedStatement,
    projectUsersList,
    sectionTreeList,
    contentSectionMap,
    sectionIdList,
    currentSectionIds,
    sectionReview: sectionPanel.sectionReviewList,
    statementOCRNavigation,
    isSideBySideModeSelected:
      selectMode && selectMode.id === ELEMENT_SELECT_MODES.SIDE_BY_SIDE.id,
    numberOfSourceElementSelected:
      sideBySideElementMap && sideBySideElementMap.sizeOfSourceMapping,
    numberOfTargetElementSelected:
      sideBySideElementMap && sideBySideElementMap.sizeOfTargetMapping,
    ocrCurrentViewPageNumber,
    sideBySideSyncScroll,
    syncScrollIds,
    sideBySideViewMode,
    workFlowStatus,
    isSnapshotReadOnly,
    tbWorkFlowStatus,
    stateData: data,
    isBatchModeSelected:
      selectMode && selectMode.id === ELEMENT_SELECT_MODES.BATCH_WITH_BANNER.id,
    editPermissionAutotieout,
  };
};

const mapDispatchToProps = {
  initStatementContent,
  setElementSelectModeSideBySideIfPossible,
  clearSideBySideModeIfPossible: clearModeIfPossible,
  fetchSelectedStatement,
  onRightStatementNavSectionClick,
  setOcrCurrentViewPageNumberDispatch,
  setTargetSyncScrollId,
  setSideBySideSyncScroll,
  setAutoTieoutStatusForBanner,
  setElementSelectModeBatchIfPossible,
};

export default withURLRouter(
  connect(mapStateToProps, mapDispatchToProps)(RightSideView),
);
